/**
 * KernelEditor — first-class kernel editing surface.
 *
 * Two modes for the same underlying draft:
 *   • Cards (default): one card per step + one render-spec card.
 *     Each step gets its own focused editor (JSON for the
 *     analytics_query spec, Python for the python code, JSON for
 *     the inputs mapping). Add / remove / reorder steps inline.
 *   • Raw JSON: a single editor over the entire kernel object,
 *     for power-user edits that span multiple steps at once.
 *
 * State model: the draft lives as a parsed kernel object. The raw
 * editor stringifies it on view switch and re-parses on every
 * keystroke (with parse-error UI so the user can recover). Saving
 * passes the parsed draft through kernelStore.saveKernel, which
 * applies the same validation the chat-side save path uses, so a
 * broken render kind or missing steps surface the same way.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CodeEditor from "./CodeEditor.jsx";
import {
    formatKernelShareWarnings,
    kernelShareWarnings,
} from "../lib/kernelShareSafety.js";
import {
    isUntouchedBlankDraft,
    KNOWN_RENDER_KINDS,
    saveKernelCloudFirst,
} from "../lib/kernelStore.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { APPROVED_PYTHON_PACKAGE_NAMES } from "../lib/pythonPackageRequirements.js";

// ── Helpers ──────────────────────────────────────────────────────

function safeStringify(obj) {
    try { return JSON.stringify(obj, null, 2); } catch { return ""; }
}

function safeParse(text) {
    try { return { ok: true, value: JSON.parse(text) }; }
    catch (err) { return { ok: false, error: err.message }; }
}

function nextStepId(steps, prefix = "s") {
    let n = 1;
    const used = new Set(steps.map((s) => s.id));
    while (used.has(`${prefix}${n}`)) n += 1;
    return `${prefix}${n}`;
}

// Prompt-rooted so scoped kernels can inject the active drilldown. PROMPT has
// no standard metrics, so its row count is computed from the event id.
export const DEFAULT_ANALYTICS_SPEC = Object.freeze({
    root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
    timeRange: { relative: { durationSeconds: String(7 * 86400) } },
    output: {
        dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" }, outputAlias: "label" },
        ],
        metrics: [
            { outputAlias: "value", computed: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, op: "AGGREGATE_OP_COUNT" } },
        ],
    },
    orderBy: [{ outputAlias: "value", direction: "DIRECTION_DESC" }],
    limit: 10,
});

export function defaultStep(kind, steps) {
    if (kind === "python") {
        return {
            kind: "python",
            id: nextStepId(steps),
            code: "result = None\n",
            inputs: {},
            packages: [],
        };
    }
    if (kind === "mcp_tool") {
        return { kind: "mcp_tool", id: nextStepId(steps), server: "", tool: "", args: {} };
    }
    if (kind === "agent") {
        return { kind: "agent", id: nextStepId(steps), prompt: "", inputs: {}, outputSchema: { type: "object" } };
    }
    return {
        kind: "analytics_query",
        id: nextStepId(steps),
        spec: structuredClone(DEFAULT_ANALYTICS_SPEC),
    };
}

// ── Step card ────────────────────────────────────────────────────

function StepCard({ step, idx, total, priorSteps = [], expanded, onToggle, onChange, onMove, onRemove }) {
    const kindLabel = step.kind === "python" ? "Python" : step.kind === "mcp_tool" ? "MCP tool" : step.kind === "agent" ? "Agent" : "Analytics query";
    return (
        <article className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)]">
            <header className="flex items-baseline gap-[8px] px-[10px] py-[8px] border-b border-[color:var(--line-soft)]">
                <button
                    type="button"
                    onClick={onToggle}
                    className="grid h-[16px] w-[16px] place-items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    title={expanded ? "Collapse" : "Expand"}
                >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" className={`h-[10px] w-[10px] transition-transform ${expanded ? "rotate-90" : ""}`}>
                        <path d="m6 3 5 5-5 5" />
                    </svg>
                </button>
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>#{idx + 1}</span>
                <span className="font-mono text-[11px] text-[color:var(--ink)]">{kindLabel}</span>
                <input
                    type="text"
                    value={step.id}
                    onChange={(e) => onChange({ ...step, id: e.target.value })}
                    className="min-w-0 flex-1 max-w-[140px] border border-[color:var(--line)] bg-transparent px-[6px] py-[1px] font-mono text-[10.5px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                    title="Step id (referenced by inputs)"
                />
                <div className="ml-auto flex items-baseline gap-[2px]">
                    <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[5px] py-[1px] text-[10px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-40" title="Move up">↑</button>
                    <button type="button" onClick={() => onMove(+1)} disabled={idx === total - 1} className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[5px] py-[1px] text-[10px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-40" title="Move down">↓</button>
                    <button type="button" onClick={onRemove} className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[5px] py-[1px] text-[10px] text-[color:var(--muted)] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)]" title="Remove step">×</button>
                </div>
            </header>
            {expanded ? (
                <div className="grid gap-[8px] p-[10px]">
                    {step.kind === "python" ? (
                        <>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Code</span>
                                <CodeEditor
                                    language="python"
                                    value={step.code || ""}
                                    onChange={(code) => onChange({ ...step, code })}
                                    autoHeight
                                />
                            </div>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Packages (loaded lazily)</span>
                                <div className="flex flex-wrap gap-x-[12px] gap-y-[4px]">
                                    {APPROVED_PYTHON_PACKAGE_NAMES.map((packageName) => (
                                        <label key={packageName} className="flex items-center gap-[5px] font-mono text-[10.5px] text-[color:var(--ink)]">
                                            <input
                                                type="checkbox"
                                                checked={step.packages?.includes(packageName) === true}
                                                onChange={(event) => {
                                                    const packages = new Set(step.packages || []);
                                                    if (event.target.checked) packages.add(packageName);
                                                    else packages.delete(packageName);
                                                    onChange({
                                                        ...step,
                                                        packages: [...packages].sort(),
                                                    });
                                                }}
                                            />
                                            {packageName}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Inputs (JSON object: name → step id)</span>
                                <CodeEditor
                                    language="json"
                                    value={safeStringify(step.inputs || {})}
                                    onChange={(text) => {
                                        const parsed = safeParse(text);
                                        if (parsed.ok && typeof parsed.value === "object") {
                                            onChange({ ...step, inputs: parsed.value });
                                        }
                                    }}
                                    autoHeight
                                />
                            </div>
                        </>
                    ) : step.kind === "mcp_tool" ? (
                        <>
                            <div className="grid grid-cols-2 gap-[8px]">
                                <label className="grid gap-[4px]">
                                    <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Server</span>
                                    <input
                                        type="text"
                                        value={step.server || ""}
                                        onChange={(e) => onChange({ ...step, server: e.target.value })}
                                        placeholder="linear"
                                        className="min-w-0 border border-[color:var(--line)] bg-transparent px-[6px] py-[2px] font-mono text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                                    />
                                </label>
                                <label className="grid gap-[4px]">
                                    <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Tool</span>
                                    <input
                                        type="text"
                                        value={step.tool || ""}
                                        onChange={(e) => onChange({ ...step, tool: e.target.value })}
                                        placeholder="search_documentation"
                                        className="min-w-0 border border-[color:var(--line)] bg-transparent px-[6px] py-[2px] font-mono text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                                    />
                                </label>
                            </div>
                            <label className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Arguments from step (optional)</span>
                                <select
                                    value={step.argsFrom || ""}
                                    onChange={(e) => onChange({ ...step, argsFrom: e.target.value || undefined })}
                                    className="min-h-[26px] border border-[color:var(--line)] px-[6px] text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                                >
                                    <option value="">None (use static arguments)</option>
                                    {priorSteps.map((s) => (
                                        <option key={s.id} value={s.id}>{s.id} · {s.kind}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Static arguments (JSON, merged under argsFrom)</span>
                                <CodeEditor
                                    language="json"
                                    value={safeStringify(step.args || {})}
                                    onChange={(text) => {
                                        const parsed = safeParse(text);
                                        if (parsed.ok && typeof parsed.value === "object") {
                                            onChange({ ...step, args: parsed.value });
                                        }
                                    }}
                                    autoHeight
                                />
                            </div>
                        </>
                    ) : step.kind === "agent" ? (
                        <>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Prompt</span>
                                <CodeEditor
                                    language="markdown"
                                    value={step.prompt || ""}
                                    onChange={(prompt) => onChange({ ...step, prompt })}
                                    autoHeight
                                />
                            </div>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Inputs (JSON object: name → step id)</span>
                                <CodeEditor
                                    language="json"
                                    value={safeStringify(step.inputs || {})}
                                    onChange={(text) => {
                                        const parsed = safeParse(text);
                                        if (parsed.ok && typeof parsed.value === "object") {
                                            onChange({ ...step, inputs: parsed.value });
                                        }
                                    }}
                                    autoHeight
                                />
                            </div>
                            <div className="grid gap-[4px]">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Output schema (JSON Schema)</span>
                                <span className={`${NOTE_CLASS} text-[10px]`}>Results must match this schema. For an MCP tool, use the tool input schema.</span>
                                <CodeEditor
                                    language="json"
                                    value={safeStringify(step.outputSchema || {})}
                                    onChange={(text) => {
                                        const parsed = safeParse(text);
                                        if (parsed.ok && typeof parsed.value === "object") {
                                            onChange({ ...step, outputSchema: parsed.value });
                                        }
                                    }}
                                    autoHeight
                                />
                            </div>
                        </>
                    ) : (
                        <div className="grid gap-[4px]">
                            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Spec (analytics_query)</span>
                            <CodeEditor
                                language="json"
                                value={safeStringify(step.spec || {})}
                                onChange={(text) => {
                                    const parsed = safeParse(text);
                                    if (parsed.ok) {
                                        onChange({ ...step, spec: parsed.value });
                                    }
                                }}
                                autoHeight
                            />
                        </div>
                    )}
                </div>
            ) : null}
        </article>
    );
}

// ── Render-spec card ─────────────────────────────────────────────

function RenderCard({ render, renderSource, steps, expanded, onToggle, onChange, onChangeSource }) {
    return (
        <article className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)]">
            <header className="flex items-baseline gap-[8px] px-[10px] py-[8px] border-b border-[color:var(--line-soft)]">
                <button
                    type="button"
                    onClick={onToggle}
                    className="grid h-[16px] w-[16px] place-items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    title={expanded ? "Collapse" : "Expand"}
                >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" className={`h-[10px] w-[10px] transition-transform ${expanded ? "rotate-90" : ""}`}>
                        <path d="m6 3 5 5-5 5" />
                    </svg>
                </button>
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Render</span>
                <span className="font-mono text-[11px] text-[color:var(--ink)]">{render?.kind || "(unset)"}</span>
            </header>
            {expanded ? (
                <div className="grid gap-[8px] p-[10px]">
                    <label className="grid gap-[4px]">
                        <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Source step</span>
                        <select
                            value={renderSource || ""}
                            onChange={(e) => onChangeSource(e.target.value || null)}
                            className="min-h-[26px] border border-[color:var(--line)] px-[6px] text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                        >
                            <option value="">None</option>
                            {steps.map((s) => (
                                <option key={s.id} value={s.id}>{s.id} · {s.kind}</option>
                            ))}
                        </select>
                    </label>
                    <div className="grid gap-[4px]">
                        <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Render spec (JSON)</span>
                        <CodeEditor
                            language="json"
                            value={safeStringify(render || {})}
                            onChange={(text) => {
                                const parsed = safeParse(text);
                                if (parsed.ok) onChange(parsed.value);
                            }}
                            autoHeight
                        />
                        {render?.kind && !KNOWN_RENDER_KINDS.has(render.kind) ? (
                            <span className="text-[11px] text-[color:var(--red,#c0392b)]">
                                Unknown render kind <code>{render.kind}</code>. Valid: {[...KNOWN_RENDER_KINDS].slice(0, 6).join(", ")}…
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </article>
    );
}

// ── Top-level editor ─────────────────────────────────────────────

export default function KernelEditor({
    kernel,
    draft: externalDraft,
    setDraft: setExternalDraft,
    view = "cards",
    onSave,
    onSaved,
    saveNotice,
}) {
    // Draft state can live here (legacy) or be lifted to the parent
    // so other surfaces — e.g. the inline KernelChat — can share it.
    // When `draft`/`setDraft` are supplied as props we wire to them
    // directly; otherwise we fall back to local state for callers
    // that just want a self-contained editor.
    const [localDraft, setLocalDraft] = useState(kernel);
    const draft = externalDraft !== undefined ? externalDraft : localDraft;
    const setDraft = setExternalDraft || setLocalDraft;

    const [rawText, setRawText] = useState(() => safeStringify(kernel));
    const [parseError, setParseError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [localSaveNotice, setLocalSaveNotice] = useState(null);
    const [localRecoveryPending, setLocalRecoveryPending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set([kernel.steps?.[0]?.id, "render"]));
    useEffect(() => {
        if (externalDraft === undefined) setLocalDraft(kernel);
        setRawText(safeStringify(kernel));
        setParseError(null);
        setSaveError(null);
        setLocalSaveNotice(null);
        setLocalRecoveryPending(false);
        setExpanded(new Set([kernel.steps?.[0]?.id, "render"]));
        // externalDraft intentionally omitted — parent owns its reset
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kernel.id]);

    // When the EXTERNAL draft changes (e.g. the chat agent ran a
    // tool that mutated it), keep rawText in sync so flipping to
    // Raw JSON shows the latest content.
    //
    // But DON'T clobber the user's in-progress raw text when the draft
    // change originated from their own typing here: onRawChange parses
    // rawText and pushes it into the (lifted) draft, which fires this
    // effect — re-stringifying canonically would reset the editor and
    // jump the cursor on every keystroke that's momentarily valid JSON,
    // making raw edits impossible (and the save appear broken). Skip the
    // resync when the current rawText already represents externalDraft;
    // only sync on a genuine external mutation. rawText is intentionally
    // omitted from deps — we react to externalDraft changes only, reading
    // the latest rawText from the closure of the render that produced them.
    useEffect(() => {
        if (externalDraft === undefined) return;
        const current = safeParse(rawText);
        if (current.ok && safeStringify(current.value) === safeStringify(externalDraft)) {
            return;
        }
        setRawText(safeStringify(externalDraft));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalDraft]);

    // When the parent toggles between "cards" and "raw" via the tab
    // strip, sync the inactive view's source-of-truth so jumping back
    // never shows stale content. Cards → Raw: re-stringify current
    // draft. Raw → Cards: parse rawText into draft if it's clean
    // (otherwise leave the draft as-is and surface the parse error).
    const lastViewRef = useRef(view);
    useEffect(() => {
        if (lastViewRef.current === view) return;
        if (view === "raw") {
            setRawText(safeStringify(draft));
            setParseError(null);
        } else {
            const parsed = safeParse(rawText);
            if (parsed.ok) {
                setDraft(parsed.value);
                setParseError(null);
            } else {
                setParseError(parsed.error);
            }
        }
        lastViewRef.current = view;
    }, [view, draft, rawText]);

    // Mutate the draft. rawText syncs via the externalDraft effect
    // above so this updater stays pure — calling setRawText
    // inside the setDraft updater would queue a setState on this
    // component while the parent (which owns externalDraft) is
    // still reconciling, triggering React's "setState in render
    // of a different component" warning.
    const updateDraft = useCallback((mutator) => {
        setDraft(mutator);
        setSaveError(null);
        setLocalSaveNotice(null);
    }, [setDraft]);

    const dirty = useMemo(() => safeStringify(draft) !== safeStringify(kernel), [draft, kernel]);

    const onRawChange = (text) => {
        setRawText(text);
        const parsed = safeParse(text);
        if (parsed.ok) {
            setDraft(parsed.value);
            setParseError(null);
        } else {
            setParseError(parsed.error);
        }
        setSaveError(null);
        setLocalSaveNotice(null);
    };

    const handleSave = async () => {
        // In raw view the editor's text is the source of truth — parse it
        // fresh at save time rather than trusting `draft`, which can lag or
        // be reset out from under us by an external resync. This guarantees
        // we persist exactly what's in the editor.
        let toSave = draft;
        if (view === "raw") {
            const parsed = safeParse(rawText);
            if (!parsed.ok) {
                setParseError(parsed.error);
                setSaveError("Fix the JSON parse error before saving.");
                return;
            }
            toSave = parsed.value;
        }
        setSaving(true);
        try {
            const saved = onSave
                ? await onSave(toSave)
                : await saveKernelCloudFirst(null, toSave);
            if (saved?.cloudSaveStatus) {
                throw new Error(saved.cloudSaveMessage || "This kernel is still stored locally.");
            }
            if (!onSave) {
                const durabilityWarning = saved?.localDurabilityWarning || null;
                setLocalRecoveryPending(Boolean(durabilityWarning));
                setLocalSaveNotice(
                    durabilityWarning
                    || formatKernelShareWarnings(kernelShareWarnings(toSave)),
                );
            }
            // Keep the lifted draft in lockstep with what we just saved so
            // a stale draft can't resurface on the next render.
            setDraft(toSave);
            onSaved?.(saved);
            setSaveError(null);
        } catch (err) {
            setSaveError(err?.message || String(err));
        } finally {
            setSaving(false);
        }
    };

    const handleRevert = () => {
        setDraft(kernel);
        setRawText(safeStringify(kernel));
        setParseError(null);
        setSaveError(null);
        if (!localRecoveryPending) setLocalSaveNotice(null);
    };

    const toggleExpanded = (key) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Step mutations — all funnel through updateDraft.
    const updateStep = (idx, nextStep) => {
        updateDraft((prev) => {
            const steps = (prev.steps || []).slice();
            steps[idx] = nextStep;
            // If the step id changed and renderSource pointed at the
            // old id, retarget renderSource so the link survives.
            const oldId = prev.steps[idx]?.id;
            const newId = nextStep.id;
            const renderSource = prev.renderSource === oldId ? newId : prev.renderSource;
            return { ...prev, steps, renderSource };
        });
    };
    const moveStep = (idx, dir) => {
        updateDraft((prev) => {
            const steps = (prev.steps || []).slice();
            const target = idx + dir;
            if (target < 0 || target >= steps.length) return prev;
            const tmp = steps[idx]; steps[idx] = steps[target]; steps[target] = tmp;
            return { ...prev, steps };
        });
    };
    const removeStep = (idx) => {
        updateDraft((prev) => ({
            ...prev,
            steps: (prev.steps || []).filter((_, i) => i !== idx),
        }));
    };
    const addStep = (kind) => {
        updateDraft((prev) => {
            const newStep = defaultStep(kind, prev.steps || []);
            const steps = [...(prev.steps || []), newStep];
            // Auto-expand the new step.
            setExpanded((prevSet) => new Set([...prevSet, newStep.id]));
            // An agent step makes the whole kernel agentic — saveKernel
            // rejects agent steps otherwise, so flip the flag on add.
            return {
                ...prev,
                steps,
                ...(kind === "agent" ? { agentic: true } : {}),
                ...(isUntouchedBlankDraft(prev) ? { renderSource: newStep.id } : {}),
            };
        });
    };
    const updateRender = (nextRender) => {
        updateDraft((prev) => ({ ...prev, render: nextRender }));
    };
    const updateRenderSource = (sourceId) => {
        updateDraft((prev) => ({ ...prev, renderSource: sourceId || null }));
    };

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            {dirty ? (
                <span className="shrink-0 text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">unsaved changes</span>
            ) : null}

            {/* Editor body — raw view stretches the CodeEditor to
                fill the remaining column height (it handles its own
                internal scroll). Cards view scrolls internally so
                the save/revert footer stays anchored at the bottom
                of the column rather than scrolling off-screen with
                a long step list. */}
            {view === "cards" ? (
                <div className="flex-1 min-h-0 overflow-y-auto pt-[14px] pb-[10px]">
                  <div className="grid gap-[10px]">
                    {draft.agentic ? (
                        <div className="flex items-baseline gap-[6px] rounded-[2px] border border-dashed border-[color:var(--line)] px-[8px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)]">
                            <span className="text-[color:var(--ink)]">Agentic</span>
                            <span aria-hidden="true" className="h-[10px] w-px bg-[color:var(--line)]" />
                            <span className="normal-case tracking-normal">Runs an LLM on each execution and may produce different results.</span>
                        </div>
                    ) : null}
                    {(draft.steps || []).map((step, i) => (
                        <StepCard
                            key={`${i}-${step.id}`}
                            step={step}
                            idx={i}
                            total={draft.steps.length}
                            priorSteps={(draft.steps || []).slice(0, i)}
                            expanded={expanded.has(step.id)}
                            onToggle={() => toggleExpanded(step.id)}
                            onChange={(s) => updateStep(i, s)}
                            onMove={(dir) => moveStep(i, dir)}
                            onRemove={() => removeStep(i)}
                        />
                    ))}
                    <div className="flex items-baseline gap-[8px]">
                        <button
                            type="button"
                            onClick={() => addStep("analytics_query")}
                            className="cursor-pointer rounded-[2px] border border-dashed border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                        >
                            + Analytics query
                        </button>
                        <button
                            type="button"
                            onClick={() => addStep("python")}
                            className="cursor-pointer rounded-[2px] border border-dashed border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                        >
                            + Python step
                        </button>
                        <button
                            type="button"
                            onClick={() => addStep("mcp_tool")}
                            className="cursor-pointer rounded-[2px] border border-dashed border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                        >
                            + MCP tool
                        </button>
                        <button
                            type="button"
                            onClick={() => addStep("agent")}
                            title="Add an agent step without tools. The kernel may produce different results on each run."
                            className="cursor-pointer rounded-[2px] border border-dashed border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                        >
                            + Agent
                        </button>
                    </div>
                    <RenderCard
                        render={draft.render}
                        renderSource={draft.renderSource}
                        steps={draft.steps || []}
                        expanded={expanded.has("render")}
                        onToggle={() => toggleExpanded("render")}
                        onChange={updateRender}
                        onChangeSource={updateRenderSource}
                    />
                  </div>
                </div>
            ) : (
                <div className="flex flex-1 min-h-0 flex-col gap-[6px] pt-[14px] pb-[10px]">
                    <CodeEditor
                        language="json"
                        value={rawText}
                        onChange={onRawChange}
                        className="flex-1 min-h-0"
                        minHeight="0"
                        maxHeight="none"
                    />
                    {parseError ? (
                        <span className="shrink-0 text-[11px] text-[color:var(--red,#c0392b)]">
                            Parse error: {parseError}
                        </span>
                    ) : null}
                </div>
            )}

            {/* Save / revert */}
            <div className="shrink-0 flex items-baseline gap-[8px] border-t border-[color:var(--line-soft)] pt-[10px] pb-[18px]">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={
                        saving
                        || (view === "raw" && parseError)
                        || (!dirty && !localRecoveryPending)
                    }
                    className="cursor-pointer rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[3px] text-[10px] uppercase tracking-[0.04em] font-medium text-[color:var(--ink)] hover:border-[color:var(--green)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {saving
                        ? localRecoveryPending ? "Retrying…" : "Saving…"
                        : localRecoveryPending ? "Retry local recovery" : "Save"}
                </button>
                <button
                    type="button"
                    onClick={handleRevert}
                    disabled={saving || !dirty}
                    className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Revert
                </button>
                {saveError ? (
                    <span className="text-[11px] text-[color:var(--red,#c0392b)]">{saveError}</span>
                ) : null}
                {saveNotice || localSaveNotice ? (
                    <span
                        role={localRecoveryPending ? "alert" : "status"}
                        className="text-[11px] leading-[1.4] text-[color:var(--muted)]"
                    >
                        {saveNotice || localSaveNotice}
                    </span>
                ) : null}
            </div>
        </div>
    );
}
